import { useCallback, useEffect, useMemo, useState } from 'react'
import { useStoreActions, useStoreState } from 'store/hooks'

export interface FieldDefinition {
    type: string
    label?: string
    valueName?: string
    aiDescription?: string
    multiLine?: boolean
    lines?: number
    text?: string
    control?: string
    options?: Array<{ value: string; label: string }>
    title?: string
    itemTitle?: string
    min?: number
    max?: number
    fields?: FieldDefinition[]
    disableInternal?: boolean
    disableExternal?: boolean
    disableFile?: boolean
}

export interface ElementDefinition {
    name: string
    identifier: string
    aiDescription?: string
    initialData: any
    fields: FieldDefinition[]
}

export const useElementDefinitions = () => {
    const selectedWebsite = useStoreState((state) => state.model.selectedWebsite)
    const websiteSave = useStoreActions((actions) => actions.model.websiteSave)

    const [elements, setElements] = useState<ElementDefinition[]>([])
    const [selectedIndex, setSelectedIndex] = useState<number>(-1)
    const [dirty, setDirty] = useState(false)

    // Parse elementDefinitions JSON from the website
    const parsedFromWebsite = useMemo(() => {
        try {
            const json = selectedWebsite?.elementDefinitions
            if (!json) return []
            const parsed = JSON.parse(json)
            return Array.isArray(parsed) ? (parsed as ElementDefinition[]) : []
        } catch {
            return []
        }
    }, [selectedWebsite?.elementDefinitions])

    // Re-parse when website data changes (reset local state)
    useEffect(() => {
        setElements(parsedFromWebsite)
        setDirty(false)
    }, [parsedFromWebsite])

    const selectedElement = selectedIndex >= 0 && selectedIndex < elements.length ? elements[selectedIndex] : undefined

    const updateElement = useCallback((index: number, updated: ElementDefinition) => {
        setElements((prev) => {
            const next = [...prev]
            next[index] = updated
            return next
        })
        setDirty(true)
    }, [])

    const addElement = useCallback(() => {
        const newElement: ElementDefinition = {
            name: 'New Element',
            identifier: 'newElement',
            initialData: {},
            fields: [],
        }
        setElements((prev) => [...prev, newElement])
        setSelectedIndex(() => {
            // will be set to the new element index after state updates
            return elements.length
        })
        setDirty(true)
    }, [elements.length])

    const removeElement = useCallback(
        (index: number) => {
            setElements((prev) => prev.filter((_, i) => i !== index))
            if (selectedIndex === index) {
                setSelectedIndex(-1)
            } else if (selectedIndex > index) {
                setSelectedIndex((prev) => prev - 1)
            }
            setDirty(true)
        },
        [selectedIndex],
    )

    const save = useCallback(async () => {
        if (!selectedWebsite) return
        const json = JSON.stringify(elements, null, 4)
        await websiteSave({
            id: selectedWebsite.id,
            createdAt: selectedWebsite.createdAt,
            realmID: selectedWebsite.realmID,
            elementDefinitions: json,
            name: selectedWebsite.name,
            previewURL: selectedWebsite.previewURL,
            productionURL: selectedWebsite.productionURL,
            webhookToken: selectedWebsite.webhookToken,
            webhookRef: selectedWebsite.webhookRef,
            webhookProjectID: selectedWebsite.webhookProjectID,
        })
        setDirty(false)
    }, [selectedWebsite, elements, websiteSave])

    return {
        elements,
        selectedIndex,
        selectedElement,
        dirty,
        setSelectedIndex,
        updateElement,
        addElement,
        removeElement,
        save,
    }
}
